import {dual} from 'effect/Function';
import * as Pipeable from 'effect/Pipeable';
import React, {createContext} from 'react';

export type Prose = string | ProseElement;

function ProseComponent(props: React.PropsWithChildren) {
    return props.children;
}
ProseComponent.displayName = 'Prose';

namespace ProseKey {
    export const source = '~Prose.source';
    export const markup = '~Prose.markup';
}

class ProseElement extends Pipeable.Class() implements React.JSX.Element {
    protected readonly [ProseKey.source]: Prose.Source;
    protected readonly [ProseKey.markup]: Prose.Markup;

    constructor(
        /**  */
        source: Prose.Source,
        /**  */
        markup: Prose.Markup = ProseComponent
    ) {
        super();
        this[ProseKey.source] = source;
        this[ProseKey.markup] = markup;
        Object.assign(this, this.render());
    }

    readonly type: any;
    readonly props: any;
    readonly key = null;

    protected render() {
        return React.createElement(this[ProseKey.markup], {
            children: this[ProseKey.source],
        });
    }

    protected toString() {
        return this[ProseKey.source];
    }
}

const DefaultProseStyle = {
    p: '',
    b: '',
    i: '',
    a: '',
};
const ProseStyleContext = createContext(DefaultProseStyle);

export namespace Prose {
    export type Element = ProseElement;
    export type Markup = React.ExoticComponent<{children: Source}> | React.FC<{children: Source}>;
    export type FormatFn = (source: Source) => Source;
    export type Hole = number | string | ProseElement;
    export type Source = string;

    export const make = (text: Prose.Source) => {
        return new ProseElement(text);
    };

    export const template = (template: TemplateStringsArray, ...holes: Prose.Hole[]) => {
        return new ProseElement(String.raw(template, ...holes.map(String)));
    };

    export const phrase = (holes: Prose.Hole[], separator: Prose.Source = ' ') => {
        return new ProseElement(holes.map(String).join(separator));
    };

    export const format = dual<
        (formatter: Prose.FormatFn) => (self: ProseElement) => ProseElement,
        (self: ProseElement, formatter: Prose.FormatFn) => ProseElement
    >(2, (self, formatter) => {
        return new ProseElement(formatter(self[ProseKey.source]), self[ProseKey.markup]);
    });

    export const StyleProvider = ProseStyleContext.Provider;

    export const toUpperCase = (self: ProseElement) => format(self, makeUpperCase);
    const makeUpperCase: FormatFn = str => str.toUpperCase();

    export const toLowerCase = (self: ProseElement) => format(self, makeLowerCase);
    const makeLowerCase: FormatFn = str => str.toLowerCase();

    export const toCapitalized = (self: ProseElement) => format(self, makeCapitalized);
    const makeCapitalized: FormatFn = str => str[0].toUpperCase() + str.slice(1).toLowerCase();

    export const toTitleCase = (self: ProseElement) => format(self, makeTitleCase);
    const makeTitleCase: FormatFn = src =>
        src
            .split(' ')
            .map(word => word.trim())
            .filter(word => word !== '')
            .map(makeCapitalized)
            .join(' ');

    export const ensureEndsWith: {
        (self: ProseElement, ending: Prose): Prose;
        (ending: Prose): (self: ProseElement) => Prose;
    } = dual(2, (self: ProseElement, ending: Prose): Prose => {
        if (self[ProseKey.source].endsWith(toSource(ending))) {
            return self;
        }
        return phrase([self, ending], '');
    });

    export const ensureStartsWith: {
        (self: ProseElement, ending: Prose): Prose;
        (ending: Prose): (self: ProseElement) => Prose;
    } = dual(2, (self: ProseElement, ending: Prose): Prose => {
        if (self[ProseKey.source].startsWith(toSource(ending))) {
            return self;
        }
        return phrase([ending, self], '');
    });
}

const toSource = (prose: Prose): Prose.Source => {
    if (prose instanceof ProseElement) {
        return prose[ProseKey.source];
    }
    return prose;
};

// // Work in progress
// type ProseStyle = typeof DefaultProseStyle;
// type Element = Data.TaggedEnum<{
//     Span: { startIdx: number; endIdx: number, element: Element };
//     List: { items: Array<{ startIdx: number; endIdx: number, element: Element }>; };
//     Styled: { html: keyof ProseStyle, element: Element };
// }>;
// namespace Element {
//     const {$is, $match, ...Impl} = Data.taggedEnum<Element>();
//     export const {List, Span, Self} = Impl;

//     export const isList = $is('List');
//     export const isSpan = $is('Span');
//     export const isStyled = $is('Styled');
// }

// function Paragraphs({children: text}: {children: Prose.Source}) {
//     const texts = text.split(/\n{1,2}/);
//     return React.createElement(ProseStyleContext.Consumer, {
//         children: ({p}) => texts.map((t, i) => React.createElement('p', {className: p, key: i, children: t})),
//     });
// }
